import { useMemo } from 'react';
import PropTypes from 'prop-types';
import CardField from 'common/components/CardField';
import styled from 'styled-components';
import { DropzoneArea } from 'react-mui-dropzone';
import { useDispatch } from 'react-redux';

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import Grid from 'material/components/Grid';
import { AttachFile } from '@mui/icons-material';

import { setSnackbarError } from 'store/actions/global';

import {
  ColorPicker,
  CustomFieldsSelect,
  EditorWysiwyg,
  ImageUploader,
  Popover,
  PreviewLandingPageFields,
} from 'common/components';
import { colors } from 'utils/constants';
import { customFieldType, imageType } from 'common/typings';

const InterfaceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 770px;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  max-width: 770px;
  padding-bottom: 15px;

  .MuiInputBase-input {
    color: ${props => props.textcolor || '#000'};
  }
`;

const HeadlineTextField = styled(TextField)`
  width: 100%;
  max-width: 770px;
  padding-bottom: 35px;

  .MuiInputBase-input {
    color: ${props => props.textcolor || '#000'};
    font-size: 1.5rem;
  }

  .MuiInputLabel-root {
    font-size: 1.5rem;
  }
`;

const ColorPickersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const ColorPickerTitle = styled(Typography)`
  margin-bottom: 10px;
  padding-right: 10px;
`;

const ContentTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 51px 0 80px;
  padding-top: 40px;
  background-color: ${props => props.bgcolor || colors.blackSqueeze};
`;

const DropzoneAreaWrapper = styled.div`
  width: 100%;
  margin: 40px 0 10px;
`;

const CheckBoxOptionsWrapper = styled.div`
  width: 570px;
  margin: 40px 0;
  padding: 20px 34px;
  background-color: ${colors.wildSand};
`;

const ImageUploaderWrapper = styled.div`
  margin-bottom: 25px;
`;

const EditorWrapper = styled.div`
  width: 100%;
  max-width: 770px;
  margin-bottom: 30px;
  background-color: ${colors.white};
`;

const AdvocatesCountWrapper = styled.div`
  width: 570px;
  margin: 20px 0;
  padding: 20px 34px;
  background-color: ${colors.wildSand};
`;

const StepTwo = ({
  actionMode,
  backgroundColor,
  buttonColor,
  customFieldsList,
  customFields,
  descriptionBodyState,
  descriptionHeadlineText,
  defaultLandingPageImage,
  defaultLogo,
  googleAnalyticsId,
  handleAddCustomField,
  handleRemoveCustomField,
  headlineText,
  isFacebookPixel,
  isGoogleAnalytics,
  logoImage,
  pixelId,
  setImages,
  setIsFacebookPixel,
  setIsGoogleAnalytics,
  setGoogleAnalyticsId,
  setLogoImage,
  setTextColor,
  setHeadlineText,
  setSecondHeadlineText,
  secondHeadlineText,
  setDescriptionBodyState,
  setDescriptionHeadlineText,
  setBackgroundColor,
  setButtonColor,
  setPixelId,
  setSocialMedia,
  setVideoUrl,
  setVimeoCode,
  setShowAdvocatesCount,
  showAdvocatesCount,
  socialMedia,
  textColor,
  version,
  videoUrl,
  vimeoCode,
  youtubeId,
}) => {
  const dispatch = useDispatch();
  const initialFiles = useMemo(() => {
    const options = [];

    if (defaultLandingPageImage) {
      const file = new File([defaultLandingPageImage], 'Action Hero', {
        type: 'text/plain',
      });

      options.push(file);
    }

    return options;
  }, [defaultLandingPageImage]);

  // Only for version4 - or any future versions we add - we want to start
  // modifying the wizard interface.
  const useLatestVersion = useMemo(() => version === 'version4', [version]);

  const handleEditorError = () => {
    dispatch(
      setSnackbarError({
        message:
          'Something was wrong with your description field. Please overwrite the field again',
      })
    );

    setDescriptionBodyState(null);
  };

  const handlePreviewIcon = (fileObject, classes) => {
    const { type, name } = fileObject.file;
    const iconProps = {
      className: classes.image,
    };

    if (type === 'image/png' || type === 'image/jpeg') {
      return (
        <img
          src={fileObject.data}
          className="MuiDropzonePreviewList-image"
          alt="landing page preview"
        />
      );
    }

    if (defaultLandingPageImage && name === 'Action Hero') {
      return (
        <img
          src={defaultLandingPageImage}
          className="MuiDropzonePreviewList-image"
          alt="landing page preview"
        />
      );
    }

    return <AttachFile {...iconProps} />;
  };

  return (
    <CardField column center wrap>
      <InterfaceWrapper>
        <ImageUploaderWrapper>
          <ImageUploader
            images={logoImage}
            defaultImageScr={defaultLogo}
            setImages={setLogoImage}
            showSaveButton={false}
          />
        </ImageUploaderWrapper>
        <ColorPickersWrapper>
          <Grid alignItems="center" container justifyContent="space-between">
            <Popover
              shouldDisplayPopover
              horizontalPosition="left"
              text="Headline and button text color; description and form text use system styles for legibility."
              verticalPosition="top"
            >
              <ColorPickerTitle>Choose text color</ColorPickerTitle>
            </Popover>
            <ColorPicker
              handlePickerChange={({ hex }) => setTextColor(hex)}
              pickerColor={textColor}
            />
          </Grid>
          <Grid alignItems="center" container justifyContent="space-between">
            <ColorPickerTitle>Choose background color</ColorPickerTitle>
            <ColorPicker
              handlePickerChange={({ hex }) => setBackgroundColor(hex)}
              pickerColor={backgroundColor}
            />
          </Grid>
          <Grid alignItems="center" container justifyContent="space-between">
            <ColorPickerTitle>Choose button color</ColorPickerTitle>
            <ColorPicker
              handlePickerChange={({ hex }) => setButtonColor(hex)}
              pickerColor={buttonColor}
            />
          </Grid>
        </ColorPickersWrapper>
        <HeadlineTextField
          fullWidth
          label="Headline Text"
          onChange={event => setHeadlineText(event.target.value)}
          required
          type="text"
          value={headlineText}
          variant="standard"
        />
        {/* Version4 and beyond don't render this on the page; the same field
            feeds the meta description and list widget descriptions instead */}
        <StyledTextField
          fullWidth
          helperText={
            useLatestVersion
              ? 'Not shown on the page; used as the meta description for search results and link previews, and as the description in list widgets.'
              : null
          }
          label={useLatestVersion ? 'Optional meta description' : 'Second Headline Text'}
          multiline={useLatestVersion}
          onChange={event => setSecondHeadlineText(event.target.value)}
          type="text"
          value={secondHeadlineText}
          variant="standard"
        />
        <DropzoneAreaWrapper>
          <DropzoneArea
            key={defaultLandingPageImage || 'no-default-image'}
            onChange={file => setImages(file)}
            acceptedFiles={['image/*']}
            dropzoneText="Drag and drop Image file or click to browse."
            filesLimit={1}
            initialFiles={initialFiles}
            getPreviewIcon={handlePreviewIcon}
          />
        </DropzoneAreaWrapper>
        <StyledTextField
          fullWidth
          label="Or add youtube link here"
          onChange={event => setVideoUrl(event.target.value)}
          type="text"
          value={videoUrl}
          variant="standard"
        />
        {youtubeId && (
          <iframe
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            frameBorder="0"
            width="829"
            height="460"
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={youtubeId}
          />
        )}
        <StyledTextField
          fullWidth
          label="Or add Vimeo embed code"
          onChange={event => setVimeoCode(event.target.value)}
          type="text"
          multiline
          value={vimeoCode}
          variant="standard"
        />
        <Typography>
          When copying the code from the vimeo page, make sure you select the Fixed option. The code
          must start with the &#60; iframe &#62; tag. If you do not see your video below after
          pasting the code, then the code was probably pasted wrong.
        </Typography>
        {vimeoCode && (
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: vimeoCode,
            }}
          />
        )}
      </InterfaceWrapper>
      <ContentTextWrapper bgcolor={colors.blackSqueeze}>
        {/* Version4 and beyond do NOT include this field */}
        {!useLatestVersion && (
          <StyledTextField
            fullWidth
            label="Description Headline"
            onChange={event => setDescriptionHeadlineText(event.target.value)}
            type="text"
            value={descriptionHeadlineText}
            variant="standard"
          />
        )}
        <EditorWrapper>
          <EditorWysiwyg
            editorState={descriptionBodyState}
            setEditorStateChange={setDescriptionBodyState}
            handleEditorError={handleEditorError}
            options={['inline', 'blockType', 'list', 'link']}
            inlineOptions={['bold', 'italic']}
            listOptions={['unordered']}
            placeholder="Issue and campaign description..."
          />
        </EditorWrapper>
      </ContentTextWrapper>
      {(!!customFieldsList?.length || !!customFields?.length) && (
        <InterfaceWrapper>
          <PreviewLandingPageFields
            buttonColor={buttonColor}
            buttonName={actionMode === 'petition' ? 'Sign Petition' : 'Send Message'}
            customFields={customFields}
            handleRemoveCustomField={handleRemoveCustomField}
            textColor={textColor}
          />
          {!!customFieldsList?.length && <span>Add custom fields to your form</span>}
          {!!customFieldsList?.length && (
            <CustomFieldsSelect
              handleAddCustomField={handleAddCustomField}
              customFieldsList={customFieldsList}
            />
          )}
        </InterfaceWrapper>
      )}
      <CheckBoxOptionsWrapper>
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox checked={socialMedia} onChange={() => setSocialMedia(!socialMedia)} />
            }
            label="Add social media share buttons"
            name="social"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isGoogleAnalytics}
                onChange={() => setIsGoogleAnalytics(prevValue => !prevValue)}
              />
            }
            label="Add Google Analytics tracking code"
            name="analytics"
          />
          {isGoogleAnalytics && (
            <StyledTextField
              fullWidth
              label="Add Google Analytics ID"
              onChange={event => setGoogleAnalyticsId(event.target.value)}
              type="text"
              value={googleAnalyticsId}
              slotProps={{ htmlInput: { maxLength: 24 } }}
              variant="standard"
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={isFacebookPixel}
                onChange={() => setIsFacebookPixel(prevValue => !prevValue)}
              />
            }
            label="Add Facebook pixel"
            name="facebook"
          />
          {isFacebookPixel && (
            <StyledTextField
              fullWidth
              label="Add Facebook Pixel ID"
              onChange={event => setPixelId(event.target.value)}
              type="text"
              value={pixelId}
              slotProps={{ htmlInput: { maxLength: 24 } }}
              variant="standard"
            />
          )}
        </FormControl>
      </CheckBoxOptionsWrapper>
      <InterfaceWrapper>
        {useLatestVersion && (
          <AdvocatesCountWrapper>
            <FormControl>
              <FormLabel component="legend">Advocates Counter</FormLabel>
              <RadioGroup
                value={showAdvocatesCount ? 'true' : 'false'}
                onChange={event => setShowAdvocatesCount(event.target.value === 'true')}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="No advocates counter (default)"
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Show a running count of action takers"
                />
              </RadioGroup>
            </FormControl>
          </AdvocatesCountWrapper>
        )}
      </InterfaceWrapper>
    </CardField>
  );
};

StepTwo.defaultProps = {
  defaultLandingPageImage: null,
  defaultLogo: null,
  descriptionBodyState: null,
};

StepTwo.propTypes = {
  actionMode: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  buttonColor: PropTypes.string.isRequired,
  customFields: PropTypes.arrayOf(customFieldType).isRequired,
  customFieldsList: PropTypes.arrayOf(customFieldType).isRequired,
  defaultLandingPageImage: PropTypes.string,
  defaultLogo: PropTypes.string,
  descriptionBodyState: PropTypes.shape({
    getCurrentContent: PropTypes.func,
  }),
  descriptionHeadlineText: PropTypes.string.isRequired,
  googleAnalyticsId: PropTypes.string.isRequired,
  handleAddCustomField: PropTypes.func.isRequired,
  handleRemoveCustomField: PropTypes.func.isRequired,
  headlineText: PropTypes.string.isRequired,
  isFacebookPixel: PropTypes.bool.isRequired,
  isGoogleAnalytics: PropTypes.bool.isRequired,
  logoImage: PropTypes.arrayOf(imageType).isRequired,
  pixelId: PropTypes.string.isRequired,
  secondHeadlineText: PropTypes.string.isRequired,
  setBackgroundColor: PropTypes.func.isRequired,
  setButtonColor: PropTypes.func.isRequired,
  setDescriptionBodyState: PropTypes.func.isRequired,
  setDescriptionHeadlineText: PropTypes.func.isRequired,
  setGoogleAnalyticsId: PropTypes.func.isRequired,
  setHeadlineText: PropTypes.func.isRequired,
  setImages: PropTypes.func.isRequired,
  setIsFacebookPixel: PropTypes.func.isRequired,
  setIsGoogleAnalytics: PropTypes.func.isRequired,
  setLogoImage: PropTypes.func.isRequired,
  setPixelId: PropTypes.func.isRequired,
  setSecondHeadlineText: PropTypes.func.isRequired,
  setShowAdvocatesCount: PropTypes.func.isRequired,
  setSocialMedia: PropTypes.func.isRequired,
  setTextColor: PropTypes.func.isRequired,
  setVideoUrl: PropTypes.func.isRequired,
  setVimeoCode: PropTypes.func.isRequired,
  showAdvocatesCount: PropTypes.bool.isRequired,
  socialMedia: PropTypes.bool.isRequired,
  textColor: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
  videoUrl: PropTypes.string.isRequired,
  vimeoCode: PropTypes.string.isRequired,
  youtubeId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
};

export default StepTwo;
